// Employee self-service loans, backed by credit-service (replaces the legacy
// payroll loan endpoints). Flow: pick an enabled product → apply → HR reviews →
// accept the agreement → funds are disbursed to your payout method → repaid from
// payroll. Interest terms come from the tenant's product config (HR-set); the
// employee never picks the rate.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/apiError';
import { Coins, Wallet, Send, Info, CheckCircle2, FileText, ShieldCheck, X } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/features/shared/components/ui/table';
import { Badge } from '@/features/shared/components/ui/badge';
import { creditApi, applicationPill, facilityPill } from '@/features/credit/api/creditApi';
import { PayEarlyDialog } from '../components/PayEarlyDialog';
import { SignaturePad, type SignaturePadHandle } from '@/features/shared/components/SignaturePad';
import type {
  CreditAgreement, CreditApplication, CreditFacility, CreditOffer, Currency,
} from '@/features/credit/types';
import { formatMoney } from '../lib/format';
import { StatCard } from '../components/StatCard';
import { getDocument, type LegalDocVersion } from '@/services/complianceApi';

// Mirrors credit-service lending.Installments so the estimate matches the server.
function estimate(principal: number, termMonths: number, annualRatePct: number, method: string) {
  if (!principal || termMonths <= 0) return { installment: principal, total: principal };
  if (annualRatePct <= 0) {
    const per = Math.ceil(principal / termMonths);
    return { installment: per, total: per * termMonths };
  }
  let per: number;
  if (method === 'reducing') {
    const r = annualRatePct / 100 / 12;
    const pow = Math.pow(1 + r, termMonths);
    per = Math.ceil((principal * r * pow) / (pow - 1));
  } else {
    const interest = principal * (annualRatePct / 100) * (termMonths / 12);
    per = Math.ceil((principal + interest) / termMonths);
  }
  return { installment: per, total: per * termMonths };
}

// A single row in the unified history, spanning the three record types.
type Row =
  | { kind: 'facility'; id: string; f: CreditFacility }
  | { kind: 'agreement'; id: string; g: CreditAgreement }
  | { kind: 'application'; id: string; a: CreditApplication };

export const MyLoansPage: React.FC = () => {
  const [offers, setOffers] = useState<CreditOffer[]>([]);
  const [applications, setApplications] = useState<CreditApplication[]>([]);
  const [agreements, setAgreements] = useState<CreditAgreement[]>([]);
  const [facilities, setFacilities] = useState<CreditFacility[]>([]);
  const [loading, setLoading] = useState(true);
  const [payEarly, setPayEarly] = useState<CreditFacility | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [docsFor, setDocsFor] = useState<CreditApplication | null>(null);

  const [productId, setProductId] = useState('');
  const [amount, setAmount] = useState('');
  const [term, setTerm] = useState('6');
  const [purpose, setPurpose] = useState('');
  const [consent, setConsent] = useState(false);
  useEffect(() => { setConsent(false); }, [productId]);
  // The versioned Data-Sharing Consent document (compliance-service): shown in a modal
  // before agreeing; the recorded grant carries the version the employee read.
  const [consentDoc, setConsentDoc] = useState<LegalDocVersion | null>(null);
  const [consentDocOpen, setConsentDocOpen] = useState(false);
  // Agreement preview: the terms are shown BEFORE "Accept & receive" (informed signing).
  const [previewAgreement, setPreviewAgreement] = useState<CreditAgreement | null>(null);
  const [signingAgreement, setSigningAgreement] = useState<CreditAgreement | null>(null);

  const openConsentDoc = async () => {
    setConsentDocOpen(true);
    if (!consentDoc) {
      try {
        setConsentDoc(await getDocument('data_sharing_consent'));
      } catch {
        /* modal shows the checkbox summary text as fallback */
      }
    }
  };

  const load = () =>
    Promise.all([
      creditApi.myOffers(),
      creditApi.myApplications(),
      creditApi.myAgreements(),
      creditApi.myFacilities(),
    ])
      .then(([o, a, g, f]) => {
        // Loans & Advances handles only CREDIT products; non-credit partner products
        // (e.g. insurance) are enrolled from their own module (My Benefits).
        setOffers(o.filter((x) => x.fulfillmentDomain === 'credit'));
        setApplications(a);
        setAgreements(g);
        setFacilities(f);
        setProductId((prev) => prev || (o[0]?.productId ?? ''));
      })
      .catch(() => { /* empty state handles it */ })
      .finally(() => setLoading(false));

  useEffect(() => { void load(); }, []);

  const offer = offers.find((o) => o.productId === productId) ?? offers[0];
  const currency: Currency = (offer?.currency ?? facilities[0]?.currency ?? 'TZS') as Currency;
  const principal = Number(amount) || 0;
  const termMonths = Number(term) || 0;
  const rate = offer?.annualRate ?? 0;
  const method = offer?.interestMethod ?? 'flat';
  const est = useMemo(
    () => estimate(principal, termMonths, rate, method),
    [principal, termMonths, rate, method],
  );

  const outstanding = facilities
    .filter((f) => f.status === 'active' || f.status === 'settling' || f.status === 'in_recovery')
    .reduce((s, f) => s + f.outstandingBalance, 0);
  const activeCount = facilities.filter((f) => f.status === 'active').length;
  const pendingCount =
    applications.filter((a) => a.status === 'eligibility_passed' || a.status === 'risk_review').length +
    agreements.filter((g) => g.status === 'pending').length;

  // Client-side guardrail hints (server enforces authoritatively).
  const capMsg = (() => {
    if (!offer || principal <= 0) return '';
    if (offer.minAmount > 0 && principal < offer.minAmount)
      return `The minimum for ${offer.name} is ${formatMoney(offer.minAmount, currency)}.`;
    if (offer.maxAmount > 0 && principal > offer.maxAmount)
      return `${offer.name} is capped at ${formatMoney(offer.maxAmount, currency)}.`;
    if (offer.maxTermMonths > 0 && termMonths > offer.maxTermMonths)
      return `The maximum term is ${offer.maxTermMonths} months.`;
    return '';
  })();

  const isPartnerOffer = !!(offer && offer.fundingModel === 'partner_funded' && offer.partnerId);

  const submit = async () => {
    if (!offer) { toast.error('No loan products are available.'); return; }
    if (principal <= 0) { toast.error('Enter an amount.'); return; }
    if (termMonths <= 0) { toast.error('Enter a repayment term.'); return; }
    if (capMsg) { toast.error(capMsg); return; }
    if (isPartnerOffer && !consent) { toast.error('Please agree to share your details with the provider to continue.'); return; }
    setSubmitting(true);
    try {
      // Partner product → record the data-sharing consent before applying; the partner
      // receives the employee's data only after this grant (credit enforces it too).
      // The grant records the consent-document version the employee read.
      if (isPartnerOffer) {
        await creditApi.grantDataSharing(offer.partnerId as string, offer.productId, consentDoc?.version);
      }
      const created = await creditApi.apply({ productId: offer.productId, amount: principal, termMonths, purpose });
      setApplications((prev) => [created, ...prev]);
      setAmount(''); setPurpose('');
      if (created.status === 'approved') toast.success('Approved! Accept the agreement to receive your funds.');
      else if (created.status === 'eligibility_passed' || created.status === 'risk_review') toast.success('Request submitted for approval.');
      else toast.error('Request submitted — it did not meet the automatic eligibility check.');
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Could not submit your request.'));
    } finally {
      setSubmitting(false);
    }
  };

  const accept = async (g: CreditAgreement, signing?: { signature_png?: string; typed_name?: string; otp_code?: string }) => {
    setAcceptingId(g.id);
    try {
      await creditApi.acceptAgreement(g.id, signing);
      toast.success('Agreement signed! Funds are released once your employer and the lender countersign.', { duration: 7000 });
      await load();
      return true;
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Could not accept the agreement.'), { duration: 6000 });
      return false;
    } finally {
      setAcceptingId(null);
    }
  };

  // Build the unified, de-duplicated history: funded facilities, then agreements
  // awaiting acceptance, then applications not yet approved (approved ones appear
  // as their agreement row).
  const rows: Row[] = [
    ...facilities.map((f): Row => ({ kind: 'facility', id: f.id, f })),
    ...agreements.filter((g) => g.status === 'pending').map((g): Row => ({ kind: 'agreement', id: g.id, g })),
    ...applications
      .filter((a) => a.status !== 'approved')
      .map((a): Row => ({ kind: 'application', id: a.id, a })),
  ];

  const selectClass =
    'w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-100';

  return (
    <div className="space-y-6">
      <div className="border-b border-border pb-6">
        <h1 className="text-h1 text-charcoal">Loans &amp; Advances</h1>
        <p className="text-muted-foreground mt-1">
          Request a loan or salary advance. Your HR team reviews every request; once approved you accept the terms and the funds are sent to your payout method.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard icon={Coins} label="Active" value={String(activeCount)} tone="info" />
        <StatCard icon={Wallet} label="Pending" value={String(pendingCount)} />
        <StatCard icon={Wallet} label="Outstanding" value={formatMoney(outstanding, currency)} />
      </div>

      {/* Request form */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <h2 className="font-semibold text-foreground">New request</h2>
          {offers.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No loan products are available for you yet. Your HR team enables lending products for the company.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">Product</label>
                  <select className={selectClass} value={offer?.productId ?? ''} onChange={(e) => setProductId(e.target.value)}>
                    {offers.map((o) => (
                      <option key={o.productId} value={o.productId}>{o.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">Amount ({currency})</label>
                  <input type="number" min={0} className={selectClass} value={amount}
                    onChange={(e) => setAmount(e.target.value)} placeholder="0" />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">Repay over (months)</label>
                  <input type="number" min={1} max={offer?.maxTermMonths || undefined} className={selectClass} value={term}
                    onChange={(e) => setTerm(e.target.value)} placeholder="6" />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">Reason (optional)</label>
                <input className={selectClass} value={purpose} onChange={(e) => setPurpose(e.target.value)}
                  placeholder="e.g. school fees, medical, rent" />
              </div>

              {/* Live estimate from the product's effective rate */}
              {principal > 0 && (
                <div className="rounded-lg border border-border bg-muted/40 px-4 py-3 text-sm">
                  <div className="flex items-start gap-2 text-muted-foreground">
                    <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <span>
                      Estimated <strong className="text-foreground">{formatMoney(est.installment, currency)}</strong>/month
                      for {termMonths} months (total <strong className="text-foreground">{formatMoney(est.total, currency)}</strong>)
                      {rate > 0 ? ` · ${rate}%/yr ${method === 'reducing' ? 'reducing balance' : 'flat'}` : ' · 0% interest'}.
                      Final terms are confirmed on approval.
                    </span>
                  </div>
                </div>
              )}
              {capMsg && <p className="text-sm text-danger">{capMsg}</p>}

              {isPartnerOffer && (
                <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-2">
                  <label className="flex items-start gap-2 text-sm text-muted-foreground">
                    <input type="checkbox" className="mt-0.5" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                    <span>
                      <strong className="text-foreground">{offer?.name}</strong> is provided by an external partner. I have read the
                      Data-Sharing Consent and agree to share my employment, salary and payroll-deduction details with the provider
                      so they can assess and service my loan. I can withdraw this consent later.
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={openConsentDoc}
                    className="ml-6 inline-flex items-center gap-1.5 text-xs font-medium text-orange-600 hover:text-orange-700"
                  >
                    <ShieldCheck className="h-3.5 w-3.5" /> Read the Data-Sharing Consent
                  </button>
                </div>
              )}

              <div className="flex justify-end">
                <Button className="gap-2" onClick={submit} disabled={submitting || !!capMsg || (isPartnerOffer && !consent)}>
                  <Send className="h-4 w-4" />
                  {submitting ? 'Submitting…' : 'Submit request'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* My history */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Item</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead className="text-right">Installment</TableHead>
                <TableHead className="text-center">Term</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-40" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading &&
                Array.from({ length: 2 }).map((_, i) => (
                  <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-6 w-full" /></TableCell></TableRow>
                ))}
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-12 text-center text-muted-foreground">
                    No requests yet. Submit one above.
                  </TableCell>
                </TableRow>
              )}
              {!loading && rows.map((row) => {
                if (row.kind === 'facility') {
                  const f = row.f;
                  return (
                    <TableRow key={`f-${f.id}`}>
                      <TableCell className="font-medium text-charcoal">Loan</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{formatMoney(f.principal, f.currency)}</TableCell>
                      <TableCell className="text-right tabular-nums font-medium text-charcoal">{formatMoney(f.outstandingBalance, f.currency)}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{formatMoney(f.installment, f.currency)}</TableCell>
                      <TableCell className="text-center tabular-nums text-muted-foreground">{f.termMonths} mo</TableCell>
                      <TableCell><Badge variant={facilityPill(f.status).variant}>{facilityPill(f.status).label}</Badge></TableCell>
                      <TableCell className="text-right">
                        {['active', 'in_arrears'].includes(f.status) && (
                          <button
                            onClick={() => setPayEarly(f)}
                            className="mr-3 inline-flex items-center gap-1 text-xs font-semibold text-orange-600 hover:text-orange-700 dark:text-orange-400"
                          >
                            Pay early
                          </button>
                        )}
                        {(() => {
                          const g = agreements.find((a) => a.applicationId === f.applicationId && a.status === 'signed');
                          return g ? (
                            <button
                              onClick={() => creditApi.downloadAgreement(g.id).catch((e) => toast.error(getApiErrorMessage(e, 'Could not download the agreement.'), { duration: 6000 }))}
                              className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
                            >
                              <FileText className="h-3.5 w-3.5" /> Agreement
                            </button>
                          ) : null;
                        })()}
                      </TableCell>
                    </TableRow>
                  );
                }
                if (row.kind === 'agreement') {
                  const g = row.g;
                  return (
                    <TableRow key={`g-${g.id}`}>
                      <TableCell className="font-medium text-charcoal">Loan offer</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{formatMoney(g.terms.principal, g.terms.currency)}</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">—</TableCell>
                      <TableCell className="text-right tabular-nums text-muted-foreground">{formatMoney(g.terms.installment, g.terms.currency)}</TableCell>
                      <TableCell className="text-center tabular-nums text-muted-foreground">{g.terms.termMonths} mo</TableCell>
                      <TableCell><Badge variant="warning">Approved — accept to receive</Badge></TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" className="gap-1.5" disabled={acceptingId === g.id} onClick={() => setPreviewAgreement(g)}>
                          <FileText className="h-4 w-4" />
                          {acceptingId === g.id ? 'Accepting…' : 'Review & accept'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                }
                const a = row.a;
                return (
                  <TableRow key={`a-${a.id}`}>
                    <TableCell className="font-medium text-charcoal">Loan request</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{formatMoney(a.requestedAmount, currency)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">—</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">—</TableCell>
                    <TableCell className="text-center tabular-nums text-muted-foreground">{a.requestedTermMonths} mo</TableCell>
                    <TableCell><Badge variant={applicationPill(a.status).variant}>{applicationPill(a.status).label}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setDocsFor(a)}>
                        <FileText className="h-4 w-4" /> Documents
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {docsFor && <ApplicationDocumentsModal app={docsFor} onClose={() => setDocsFor(null)} />}

      {/* Data-Sharing Consent document (versioned; the grant records what was read) */}
      {consentDocOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setConsentDocOpen(false)}>
          <div className="max-h-[85vh] w-full max-w-2xl overflow-auto rounded-2xl bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between">
              <div>
                <h3 className="flex items-center gap-2 font-semibold text-foreground">
                  <ShieldCheck className="h-5 w-5 text-orange-500" /> {consentDoc?.title ?? 'Data-Sharing Consent'}
                </h3>
                {consentDoc && <p className="text-xs text-muted-foreground mt-0.5">Version {consentDoc.version}</p>}
              </div>
              <button onClick={() => setConsentDocOpen(false)} className="rounded-lg p-1 text-muted-foreground hover:bg-muted"><X className="h-4 w-4" /></button>
            </div>
            {consentDoc ? (
              <div className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">{consentDoc.body}</div>
            ) : (
              <Skeleton className="h-40 w-full" />
            )}
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setConsentDocOpen(false)}>Close</Button>
              <Button
                className="gap-1.5"
                onClick={() => { setConsent(true); setConsentDocOpen(false); }}
              >
                <CheckCircle2 className="h-4 w-4" /> I have read it — I agree
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Agreement preview — the terms are shown BEFORE accepting (informed signing) */}
      {previewAgreement && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setPreviewAgreement(null)}>
          <div className="w-full max-w-md rounded-2xl bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between">
              <h3 className="flex items-center gap-2 font-semibold text-foreground">
                <FileText className="h-5 w-5 text-orange-500" /> Your loan agreement
              </h3>
              <button onClick={() => setPreviewAgreement(null)} className="rounded-lg p-1 text-muted-foreground hover:bg-muted"><X className="h-4 w-4" /></button>
            </div>
            {(() => {
              const t = previewAgreement.terms;
              const rows: Array<[string, string]> = [
                ['Loan amount', formatMoney(t.principal, t.currency)],
                ['Interest', t.annualRate > 0 ? `${t.annualRate}%/yr · ${t.interestMethod === 'reducing' ? 'reducing balance' : 'flat'}` : 'Interest-free'],
                ['Monthly instalment', formatMoney(t.installment, t.currency)],
                ['Term', `${t.termMonths} months`],
                ['Total to repay', formatMoney(t.totalRepayable, t.currency)],
              ];
              return (
                <div className="divide-y divide-border rounded-lg border border-border">
                  {rows.map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between px-4 py-2.5 text-sm">
                      <span className="text-muted-foreground">{k}</span>
                      <span className="font-medium text-foreground tabular-nums">{v}</span>
                    </div>
                  ))}
                </div>
              );
            })()}
            <p className="mt-3 text-xs text-muted-foreground">
              Repayments are deducted from your salary each payroll. Signing opens the countersign chain —
              your employer and the lender sign after you, and funds are released once everyone has signed.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPreviewAgreement(null)}>Not now</Button>
              <Button
                className="gap-1.5"
                onClick={() => { const g = previewAgreement; setPreviewAgreement(null); setSigningAgreement(g); }}
              >
                <FileText className="h-4 w-4" /> Continue to sign
              </Button>
            </div>
          </div>
        </div>
      )}
      {signingAgreement && (
        <SignAgreementModal
          agreement={signingAgreement}
          busy={acceptingId === signingAgreement.id}
          onClose={() => setSigningAgreement(null)}
          onSign={async (signing) => {
            const ok = await accept(signingAgreement, signing);
            if (ok) setSigningAgreement(null);
          }}
        />
      )}
      {payEarly && (
        <PayEarlyDialog
          facility={payEarly}
          onClose={() => setPayEarly(null)}
          onStarted={() => { /* balances update after the payroll/callback settles */ }}
        />
      )}
    </div>
  );
};

export default MyLoansPage;

// Origination documents for a loan request: the partner's required checklist,
// what's already attached, upload (PDF/JPG/PNG/WebP, 5MB) and download. Both
// the employee and the funding partner keep the same records.
const ApplicationDocumentsModal: React.FC<{ app: CreditApplication; onClose: () => void }> = ({ app, onClose }) => {
  const [docs, setDocs] = useState<{ id: string; doc_key: string; label: string; file_name: string; size_bytes: number; uploaded_at: string }[]>([]);
  const [required, setRequired] = useState<{ key: string; label: string }[]>([]);
  const [reqStates, setReqStates] = useState<Record<string, { status: string; note?: string }>>({});
  const [loading, setLoading] = useState(true);
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const fileRef = React.useRef<HTMLInputElement>(null);
  const pendingRef = React.useRef<{ key: string; label: string } | null>(null);

  const load = React.useCallback(async () => {
    try {
      const res = await creditApi.applicationDocuments(app.id);
      setDocs(res.documents);
      setRequired(res.required);
      setReqStates(res.states ?? {});
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not load documents.'));
    } finally {
      setLoading(false);
    }
  }, [app.id]);
  useEffect(() => { void load(); }, [load]);

  const pickFile = (key: string, label: string) => {
    pendingRef.current = { key, label };
    fileRef.current?.click();
  };
  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const slot = pendingRef.current;
    if (!file || !slot) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('The file is larger than 5MB.'); return; }
    setUploadingKey(slot.key);
    try {
      await creditApi.uploadApplicationDocument(app.id, file, slot.key, slot.label);
      toast.success(`${slot.label} uploaded.`);
      await load();
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Upload failed.'));
    } finally {
      setUploadingKey(null);
    }
  };

  const uploadedKeys = new Set(docs.map((d) => d.doc_key));
  const extras = docs.filter((d) => !required.some((r) => r.key === d.doc_key));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-h-[85vh] w-full max-w-lg overflow-auto rounded-2xl bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="flex items-center gap-2 font-semibold text-foreground">
              <FileText className="h-5 w-5 text-orange-500" /> Loan documents
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Documents the lender needs for this request. PDF, JPG, PNG or WebP up to 5MB.
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-5 w-5" /></button>
        </div>
        <input ref={fileRef} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} />
        {loading ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <div className="space-y-2">
            {required.map((r) => {
              const done = uploadedKeys.has(r.key);
              const doc = docs.find((d) => d.doc_key === r.key);
              return (
                <div key={r.key} className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
                  <div className="flex min-w-0 items-center gap-2.5">
                    {done
                      ? <CheckCircle2 className="h-[18px] w-[18px] shrink-0 text-success" />
                      : <span className="h-4 w-4 shrink-0 rounded-full border-2 border-border" />}
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{r.label}</p>
                      {doc && <p className="truncate text-xs text-muted-foreground">{doc.file_name} · {(doc.size_bytes / 1024).toFixed(0)} KB</p>}
                      {(() => { const st = reqStates[r.key]; return st?.status === 'rejected' ? (
                        <p className="mt-0.5 text-xs font-medium text-danger">Correction requested{st.note ? `: ${st.note}` : ''} — please upload a new version.</p>
                      ) : st?.status === 'under_review' ? (
                        <p className="mt-0.5 text-xs text-warning">Being reviewed by the lender.</p>
                      ) : null; })()}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {doc && (
                      <Button size="sm" variant="ghost" onClick={() => creditApi.downloadApplicationDocument(app.id, doc.id, doc.file_name).catch((err) => toast.error(getApiErrorMessage(err, 'Could not download.')))}>
                        Download
                      </Button>
                    )}
                    <Button size="sm" variant="outline" disabled={uploadingKey === r.key} onClick={() => pickFile(r.key, r.label)}>
                      {uploadingKey === r.key ? 'Uploading…' : done ? 'Replace' : 'Upload'}
                    </Button>
                  </div>
                </div>
              );
            })}
            {extras.map((d) => (
              <div key={d.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
                <div className="flex min-w-0 items-center gap-2.5">
                  <CheckCircle2 className="h-[18px] w-[18px] shrink-0 text-success" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground">{d.label}</p>
                    <p className="truncate text-xs text-muted-foreground">{d.file_name} · {(d.size_bytes / 1024).toFixed(0)} KB</p>
                  </div>
                </div>
                <Button size="sm" variant="ghost" onClick={() => creditApi.downloadApplicationDocument(app.id, d.id, d.file_name).catch((err) => toast.error(getApiErrorMessage(err, 'Could not download.')))}>
                  Download
                </Button>
              </div>
            ))}
            {required.length === 0 && extras.length === 0 && (
              <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
                No documents are required for this product.
              </p>
            )}
            <div className="pt-1">
              <Button size="sm" variant="outline" disabled={uploadingKey === 'other'} onClick={() => pickFile('other', 'Additional document')}>
                {uploadingKey === 'other' ? 'Uploading…' : 'Attach another document'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// KaziPeople Sign — the employee's native signing step: draw a signature (full
// width pad), type the legal name, verify the emailed one-time code, confirm
// intent, sign. The drawn image is the visible mark; the evidence is the
// sealed record the backend keeps.
const SignAgreementModal: React.FC<{
  agreement: CreditAgreement;
  busy: boolean;
  onClose: () => void;
  onSign: (signing: { signature_png?: string; typed_name?: string; otp_code?: string }) => Promise<void>;
}> = ({ agreement, busy, onClose, onSign }) => {
  const padRef = React.useRef<SignaturePadHandle | null>(null);
  const [typedName, setTypedName] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [consented, setConsented] = useState(false);

  const sendOtp = async () => {
    setSendingOtp(true);
    try {
      await creditApi.requestAcceptOtp(agreement.id);
      setOtpSent(true);
      toast.success('Code sent to your email.');
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, 'Could not send the code.'), { duration: 6000 });
    } finally {
      setSendingOtp(false);
    }
  };

  const canSign = hasInk && typedName.trim().length >= 3 && otp.trim().length >= 4 && consented && !busy;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="max-h-[92vh] w-full max-w-lg overflow-auto rounded-2xl bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="flex items-center gap-2 font-semibold text-foreground">
              <FileText className="h-5 w-5 text-orange-500" /> Sign your loan agreement
            </h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {formatMoney(agreement.terms.principal, agreement.terms.currency)} · {agreement.terms.termMonths} months
            </p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-muted-foreground hover:bg-muted"><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <p className="mb-1.5 text-sm font-medium text-foreground">1. Draw your signature</p>
            <SignaturePad padRef={padRef} onChange={(empty) => setHasInk(!empty)} />
          </div>
          <div>
            <p className="mb-1.5 text-sm font-medium text-foreground">2. Type your full legal name</p>
            <input
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="e.g. David Mwangi"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>
          <div>
            <p className="mb-1.5 text-sm font-medium text-foreground">3. Verify it&apos;s you</p>
            <div className="flex gap-2">
              <input
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="6-digit code"
                inputMode="numeric"
                className="w-36 rounded-lg border border-border bg-background px-3 py-2 text-sm tracking-widest text-foreground outline-none focus:ring-2 focus:ring-orange-400"
              />
              <Button variant="outline" size="sm" disabled={sendingOtp} onClick={sendOtp}>
                {sendingOtp ? 'Sending…' : otpSent ? 'Resend code' : 'Email me a code'}
              </Button>
            </div>
            {otpSent && <p className="mt-1 text-xs text-muted-foreground">We emailed a one-time code to your address. It expires in 10 minutes.</p>}
          </div>
          <label className="flex items-start gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={consented} onChange={(e) => setConsented(e.target.checked)} className="mt-0.5" />
            <span>
              I reviewed this agreement, the signature shown is mine, and I intend to sign it electronically.
              The completed document is retained as part of my loan record.
            </span>
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            className="gap-1.5"
            disabled={!canSign}
            onClick={() => {
              const png = padRef.current?.toPNG() ?? undefined;
              void onSign({ signature_png: png, typed_name: typedName.trim(), otp_code: otp.trim() });
            }}
          >
            <CheckCircle2 className="h-4 w-4" /> {busy ? 'Signing…' : 'Sign and Complete'}
          </Button>
        </div>
      </div>
    </div>
  );
};
